// AI-derived highlights record the run and moment they came from. The API uses this to update
// the earlier AI highlight for the same moment instead of adding a duplicate on every re-run.
const MOMENT_KEY = /^[A-Za-z0-9_.:-]{1,64}$/
const momentKey = id => MOMENT_KEY.test(id || '') ? id : null

// A stable key from the analysis (same on a re-run of the same evidence) is preferred. A job-less
// review's moment IDs (e000, w000-f1) repeat across reviews, so they are prefixed with its run.
const draftMomentKey = (moment, analysisJobId, runId) => {
  if (momentKey(moment.moment_key)) return moment.moment_key
  if (!analysisJobId && /^[a-f0-9]{16,}$/.test(runId || '') && momentKey(moment.id)) return `${runId.slice(0, 16)}:${moment.id}`
  return momentKey(moment.id)
}

export const aiHighlightDraft = (moment, { playerId = '', analysisJobId = null, runId = null, groups = [] } = {}) => {
  const draft = {
    name: moment.title,
    description: `${moment.observation}\n${moment.attribution}`,
    start: moment.start_seconds,
    end: moment.end_seconds
  }
  return {
    ...draft,
    playerId,
    groups: [...groups],
    origin: 'ai',
    analysisJobId: analysisJobId || null,
    aiMomentKey: draftMomentKey(moment, analysisJobId, runId),
    // What the analysis proposed, so an owner's edits before the first save are recognised.
    aiDraft: draft
  }
}

const text = value => String(value ?? '').trim()

// Changing the name, description or range before saving makes the highlight the owner's own.
export const aiDraftEdited = h => text(h.name) !== text(h.aiDraft.name) ||
  text(h.description) !== text(h.aiDraft.description) ||
  Number(h.start) !== Number(h.aiDraft.start) || Number(h.end) !== Number(h.aiDraft.end)

// Only drafts made from an analysis moment carry provenance; copies of saved highlights do not.
export const aiProvenance = h => h?.origin === 'ai' && h.aiDraft
  ? { origin: aiDraftEdited(h) ? 'manual' : 'ai', analysis_job_id: h.analysisJobId || null, ai_moment_key: h.aiMomentKey || null }
  : {}

export const savedHighlightMessage = saved => {
  if (saved.saveResult === 'updated') {
    return saved.origin === 'manual'
      ? `Saved your edits over the earlier AI highlight for this moment: ${saved.name}`
      : `Updated the earlier AI highlight for this moment: ${saved.name}`
  }
  if (saved.saveResult === 'kept') {
    return saved.origin === 'manual'
      ? `This moment is already saved as “${saved.name}”, which you edited, so it was left unchanged.`
      : `This moment is already saved from a newer analysis: ${saved.name}`
  }
  return `Successfully created highlight: ${saved.name}`
}

// The newest run per player is the current result; older runs stay available in the analysis dialog.
export const latestRuns = jobs => jobs.filter((job, index) => jobs.findIndex(other => other.player_id === job.player_id) === index)

// Game-page analysis needs this game's jersey and uniform; the analysis dialog can supply them per run.
export const hasGameAppearance = player => Boolean(text(player?.jersey) && text(player?.uniform))

export const canRerun = (jobs, players = []) => jobs.length > 0 &&
  !jobs.some(job => ['queued', 'launching', 'running'].includes(job.status)) &&
  players.some(hasGameAppearance)
